// 단가 줄 → 표준 상품 검수 요청 대화상자 — 품목·공종·단위는 읽기만, 분류·브랜드·모델·바코드·속성·링크를 받는다.

import { useEffect, useMemo, useState, useTransition } from "react";
import { toast } from "sonner";

import {
  brandCandidates,
  CATALOG_COLOR_KEY,
  categoryLabel,
  foldBrand,
  isCatalogLink,
  isGtin13,
  resolveBrand,
} from "@buildpy/core/modules/catalog/rules";
import type { CatalogAttrDef, CatalogBrand, CatalogBrandMode, CatalogForm } from "@buildpy/core/modules/catalog/types";
import { Button } from "@buildpy/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import Field from "@/components/field";
import { createOrgBrand, getCatalogForm, submitCatalogProduct } from "@/api/actions/catalog";
import { settleRedirect } from "@/lib/redirect-error";

export interface CatalogSubmitTarget {
  id: string;
  item: string;
  scope: string;
  tradeName: string;
  unit: string;
}

export const CATALOG_LINK_NOTE = "링크는 저장만 하고 읽지 않습니다";

// 바코드 칸 오류 — 비우면 없음, 13자리가 아니면 자리 수, 검사 자리가 틀리면 그 문구.
export const gtinError = (text: string): string | null => {
  const code = text.trim();
  if (code === "") return null;
  if (!/^\d{13}$/.test(code)) return "바코드는 숫자 13자리로 넣어 주세요";
  return isGtin13(code) ? null : "마지막 검사 자리가 맞지 않습니다 — 바코드를 다시 확인해 주세요";
};

export const linkError = (text: string): string | null =>
  text.trim() === "" || isCatalogLink(text.trim()) ? null : "http:// 또는 https:// 주소만 넣을 수 있습니다";

export type AttrDraft = Record<string, string | string[]>;

// 분류별 속성 칸 — 고르기·숫자·글자·여러 개 고르기(색상은 colors 로 간다).
export const CatalogAttrFields = ({ defs, values, pending, onSet }: { defs: readonly CatalogAttrDef[]; values: AttrDraft; pending: boolean; onSet: (key: string, value: string | string[]) => void }) => {
  const field = (def: CatalogAttrDef) => {
    const label = def.unit ? `${def.label} (${def.unit})` : def.label;
    if (def.type === "pills") {
      const current = Array.isArray(values[def.key]) ? (values[def.key] as string[]) : [];
      return (
        <div key={def.key} className="flex min-w-0 flex-col gap-y-1 sm:col-span-2" data-catalog-attr={def.key}>
          <span className="text-xs text-muted-foreground">{label}</span>
          <div aria-label={label} className="flex flex-wrap gap-1.5" role="group">
            {(def.options ?? []).map((option) => (
              <Button
                key={option}
                aria-pressed={current.includes(option)}
                disabled={pending}
                type="button"
                variant="chip"
                onClick={() => onSet(def.key, current.includes(option) ? current.filter((v) => v !== option) : [...current, option])}>
                {option}
              </Button>
            ))}
          </div>
        </div>
      );
    }
    const value = typeof values[def.key] === "string" ? (values[def.key] as string) : "";
    if (def.type === "select") {
      return (
        <Field key={def.key} className="min-w-0" label={label}>
          <SimpleSelect
            aria-label={label}
            disabled={pending}
            options={[{ value: "", label: "고르지 않음" }, ...(def.options ?? []).map((o) => ({ value: o, label: o }))]}
            value={value}
            onValueChange={(next) => onSet(def.key, next)}
          />
        </Field>
      );
    }
    return (
      <Field key={def.key} className="min-w-0" label={label}>
        <Input
          aria-label={label}
          disabled={pending}
          inputMode={def.type === "number" ? "decimal" : undefined}
          name={`catalog-attr-${def.key}`}
          value={value}
          onChange={(event) => onSet(def.key, event.target.value)}
        />
      </Field>
    );
  };

  return (
    <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2" data-testid="catalog-attrs">
      {defs.map(field)}
    </div>
  );
};

interface BodyProps {
  target: CatalogSubmitTarget;
  form: CatalogForm;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (fields: Parameters<typeof submitCatalogProduct>[1]) => void;
  onAddBrand: (name: string) => Promise<CatalogBrand | null>;
}

// 대화상자 본문 — 렌더 시험이 Dialog(포털) 없이 그릴 수 있게 따로 둔다.
export const CatalogSubmitBody = ({ target, form, pending, onCancel, onSubmit, onAddBrand }: BodyProps) => {
  const [categoryId, setCategoryId] = useState("");
  const [brandMode, setBrandMode] = useState<CatalogBrandMode>("BRAND");
  const [brandText, setBrandText] = useState("");
  const [brandId, setBrandId] = useState<string | null>(null);
  const [model, setModel] = useState("");
  const [gtin, setGtin] = useState("");
  const [link, setLink] = useState("");
  const [attrs, setAttrs] = useState<AttrDraft>({});

  const category = form.categories.find((c) => c.id === categoryId) ?? null;
  const picked = brandId === null ? null : (form.brands.find((b) => b.id === brandId) ?? null);
  const resolved = useMemo(() => resolveBrand(brandText, form.brands, form.aliases), [brandText, form.brands, form.aliases]);
  const candidates = useMemo(
    () => (picked ? [] : brandCandidates(brandText, form.brands, form.aliases)),
    [picked, brandText, form.brands, form.aliases]
  );
  const brand = picked ?? resolved;
  const gtinMessage = gtinError(gtin);
  const linkMessage = linkError(link);
  const missing = categoryId === "" ? "분류를 골라 주세요" : brandMode === "BRAND" && !brand ? "브랜드를 고르거나 「일반」·「모름」 을 눌러 주세요" : null;
  const blocked = missing !== null || gtinMessage !== null || linkMessage !== null;

  const typeBrand = (text: string) => {
    setBrandText(text);
    setBrandId(null);
    setBrandMode("BRAND");
  };

  const pickBrand = (next: CatalogBrand) => {
    setBrandId(next.id);
    setBrandText(next.name);
    setBrandMode("BRAND");
  };

  const pickMode = (mode: CatalogBrandMode) => {
    setBrandMode(mode);
    if (mode !== "BRAND") {
      setBrandId(null);
      setBrandText("");
    }
  };

  const setAttr = (key: string, value: string | string[]) => setAttrs((prev) => ({ ...prev, [key]: value }));

  const submit = () => {
    if (blocked || pending) return;
    const attrValues: Record<string, unknown> = {};
    let colors: string[] = [];
    for (const def of category?.attrs ?? []) {
      const value = attrs[def.key];
      if (value === undefined) continue;
      if (def.key === CATALOG_COLOR_KEY && Array.isArray(value)) colors = value;
      else attrValues[def.key] = value;
    }
    onSubmit({
      category_id: categoryId,
      brand_mode: brandMode,
      brand_id: brandMode === "BRAND" ? (brand?.id ?? null) : null,
      model: model.trim(),
      gtin: gtin.trim(),
      attrs: attrValues,
      colors,
      link: link.trim(),
    });
  };

  return (
    <div className="flex min-w-0 flex-col gap-y-4" data-testid="catalog-submit-form">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-border px-3 py-2 text-sm" data-testid="catalog-submit-item">
        <dt className="text-muted-foreground">품목</dt>
        <dd className="min-w-0 break-words font-medium">
          {target.scope && <span className="text-muted-foreground">{target.scope} · </span>}
          {target.item}
        </dd>
        <dt className="text-muted-foreground">공종</dt>
        <dd className="min-w-0 break-words">{target.tradeName}</dd>
        <dt className="text-muted-foreground">단위</dt>
        <dd className="min-w-0 break-words">{target.unit || "-"}</dd>
      </dl>

      <Field label="분류 (필수)">
        <SimpleSelect
          aria-label="분류"
          disabled={pending}
          options={[
            { value: "", label: "분류 고르기" },
            ...form.categories.map((c) => ({ value: c.id, label: categoryLabel(c.path) })),
          ]}
          value={categoryId}
          onValueChange={(next) => {
            setCategoryId(next);
            setAttrs({});
          }}
        />
      </Field>

      <div className="flex min-w-0 flex-col gap-y-1.5" data-testid="catalog-brand">
        <Field label="브랜드 (필수)">
          <Input
            aria-label="브랜드"
            autoComplete="off"
            disabled={pending || brandMode !== "BRAND"}
            name="catalog-brand"
            placeholder={brandMode === "GENERIC" ? "일반 (무브랜드)" : brandMode === "UNKNOWN" ? "모름" : "예) 대림바스"}
            value={brandText}
            onChange={(event) => typeBrand(event.target.value)}
          />
        </Field>
        {brandMode === "BRAND" && !picked && resolved && foldBrand(brandText) !== foldBrand(resolved.name) && (
          <p className="text-xs text-muted-foreground" data-testid="catalog-brand-alias">
            「{brandText.trim()}」 는 「{resolved.name}」 로 맞춥니다
          </p>
        )}
        {candidates.length > 0 && !resolved && (
          <div aria-label="브랜드 후보" className="flex flex-wrap gap-1.5" role="group">
            {candidates.map(({ brand: candidate, via }) => (
              <Button key={candidate.id} disabled={pending} type="button" variant="chip" onClick={() => pickBrand(candidate)}>
                {candidate.name}
                {via && <span className="text-xs opacity-85">({via})</span>}
                {candidate.org_id !== null && <span className="text-xs opacity-85">우리 회사</span>}
              </Button>
            ))}
          </div>
        )}
        <div aria-label="브랜드 없음" className="flex flex-wrap gap-1.5" role="group">
          <Button aria-pressed={brandMode === "GENERIC"} disabled={pending} type="button" variant="chip" onClick={() => pickMode(brandMode === "GENERIC" ? "BRAND" : "GENERIC")}>
            일반 (무브랜드)
          </Button>
          <Button aria-pressed={brandMode === "UNKNOWN"} disabled={pending} type="button" variant="chip" onClick={() => pickMode(brandMode === "UNKNOWN" ? "BRAND" : "UNKNOWN")}>
            모름
          </Button>
          {brandMode === "BRAND" && brandText.trim() !== "" && !brand && (
            <Button
              data-testid="catalog-brand-add"
              disabled={pending}
              size="compact"
              type="button"
              variant="outline"
              onClick={() => {
                void onAddBrand(brandText).then((created) => {
                  if (created) pickBrand(created);
                });
              }}>
              새 브랜드 추가 (우리 회사만)
            </Button>
          )}
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
        <Field className="min-w-0" label="모델명">
          <Input aria-label="모델명" disabled={pending} maxLength={80} name="catalog-model" value={model} onChange={(event) => setModel(event.target.value)} />
        </Field>
        <Field className="min-w-0" label="바코드 (13자리)">
          <Input
            aria-label="바코드"
            disabled={pending}
            errorMessage={gtinMessage ?? undefined}
            inputMode="numeric"
            maxLength={13}
            name="catalog-gtin"
            value={gtin}
            onChange={(event) => setGtin(event.target.value.replace(/\s/g, ""))}
          />
        </Field>
      </div>

      {category && category.attrs.length > 0 && (
        <CatalogAttrFields defs={category.attrs} pending={pending} values={attrs} onSet={setAttr} />
      )}

      <div className="flex min-w-0 flex-col gap-y-1">
        <Field label="상품 링크">
          <Input
            aria-label="상품 링크"
            disabled={pending}
            errorMessage={linkMessage ?? undefined}
            inputMode="url"
            maxLength={500}
            name="catalog-link"
            placeholder="https://"
            value={link}
            onChange={(event) => setLink(event.target.value)}
          />
        </Field>
        <p className="text-xs text-muted-foreground">{CATALOG_LINK_NOTE}</p>
      </div>

      {missing && <p className="text-xs text-muted-foreground" data-testid="catalog-submit-missing">{missing}</p>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button disabled={pending} size="sm" type="button" variant="ghost" onClick={onCancel}>
          취소
        </Button>
        <Button data-testid="catalog-submit" disabled={blocked || pending} size="sm" type="button" onClick={submit}>
          {pending ? "보내는 중…" : "검수 요청"}
        </Button>
      </div>
    </div>
  );
};

interface Props {
  target: CatalogSubmitTarget | null;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}

const CatalogSubmitDialog = ({ target, onOpenChange, onDone }: Props) => {
  const [form, setForm] = useState<CatalogForm | null>(null);
  const [failed, setFailed] = useState(false);
  const [pending, startTransition] = useTransition();
  const open = target !== null;

  useEffect(() => {
    if (!open || form) return;
    let alive = true;
    getCatalogForm()
      .then((loaded) => {
        if (alive) setForm(loaded as CatalogForm);
      })
      .catch((caught: unknown) => {
        if (settleRedirect(caught)) return;
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [open, form]);

  const addBrand = async (name: string): Promise<CatalogBrand | null> => {
    try {
      const result = await createOrgBrand(name);
      if (!result.ok) {
        toast.error(result.message);
        return null;
      }
      const brand = result.brand as CatalogBrand;
      if (result.existed) toast.info(`있는 브랜드 「${brand.name}」 를 골랐습니다`);
      setForm((prev) => (prev && !prev.brands.some((b) => b.id === brand.id) ? { ...prev, brands: [...prev.brands, brand] } : prev));
      return brand;
    } catch (caught) {
      if (!settleRedirect(caught)) toast.error("브랜드를 추가하지 못했습니다. 잠시 후 다시 시도하세요.");
      return null;
    }
  };

  const submit = (fields: Parameters<typeof submitCatalogProduct>[1]) => {
    if (!target) return;
    startTransition(async () => {
      try {
        const result = await submitCatalogProduct(target.id, fields);
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success("검수 요청을 보냈습니다 — buildpy가 확인하면 표준 상품에 연결됩니다");
        onOpenChange(false);
        onDone();
      } catch (caught) {
        if (!settleRedirect(caught)) toast.error("검수 요청을 보내지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-3 p-4 sm:p-6" data-testid="catalog-submit-dialog">
        <DialogHeader>
          <DialogTitle>표준 상품으로 올리기</DialogTitle>
          <DialogDescription>buildpy가 확인해 표준 상품에 연결합니다. 모르는 칸은 비워 두세요.</DialogDescription>
        </DialogHeader>
        {target && form && (
          <CatalogSubmitBody key={target.id} form={form} pending={pending} target={target} onAddBrand={addBrand} onCancel={() => onOpenChange(false)} onSubmit={submit} />
        )}
        {target && !form && (
          <p className="text-sm text-muted-foreground" role="status">
            {failed ? "등록 양식을 읽지 못했습니다. 닫고 다시 열어 주세요." : "불러오는 중…"}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CatalogSubmitDialog;
